import * as React from 'react';

import classNames from 'classnames';
import { inject, observer } from 'mobx-react';

import { getConfig } from '@constants';

import { EStore } from '@enums';

import { getFromLocalStorage, getLocalizedTime, setToLocalStorage } from '@utils';

import { Translate } from '@decorators';

import { AppsStore } from '@stores';

import { ContentPanel, RatingBar } from '@components';

import { IAppRatingBarProps, IAppRatingBarState, ILocalStorageData, ILocalStorageRating } from './types';

import * as s from './AppRatingBar.scss';

const VOTING_MESSAGE_VISIBILITY_TIMEOUT = 2500;

@inject(
	EStore.CURRENT_USER,
)
@observer
@Translate('components.AppSeoPage.AppRatingBar')
export class AppRatingBar extends React.PureComponent<IAppRatingBarProps, IAppRatingBarState> {

	state: IAppRatingBarState = {
		voteTime: 0,
		appRating: this.props.appRating || 0,
		hasVoted: false,
	};

	localStorageRating: ILocalStorageRating = {
		generatedKey: `my_app-rating:u${this.props.currentUserStore?.currentUser?.id || ''};//`,
		set(appId: string, value: string, time: number) {
			const currentRatingList = this.getAll() || [];
			const newAppRating = { appId, rating: value, time };
			let isUpdate = false;
			const newRatingList = currentRatingList.map((appRating: ILocalStorageData) => {
				if (appRating.appId === appId) {
					isUpdate = true;
					return newAppRating;
				}
				return appRating;
			});

			const newValue = isUpdate ? newRatingList : [...currentRatingList, newAppRating];

			setToLocalStorage(this.generatedKey, JSON.stringify(newValue));
		},
		getAll() {
			const ratingString = getFromLocalStorage(this.generatedKey);
			return ratingString?.length ? JSON.parse(ratingString) : [];
		},
		get(appId: string): ILocalStorageData {
			const ratingString = getFromLocalStorage(this.generatedKey);
			const ratingsData: ILocalStorageData[] = ratingString?.length ? JSON.parse(ratingString) : [];
			const appRatingData: ILocalStorageData = ratingsData.filter((app: ILocalStorageData) => app.appId === appId)[0];

			return {
				appId: appRatingData?.appId || '',
				rating: Number(appRatingData?.rating) || 0,
				time: Number(appRatingData?.time) || 0,
			};
		},
	};

	componentDidMount(): void {
		this.updateRating();
	}

	componentDidUpdate(prevProps: Readonly<IAppRatingBarProps>) {
		if (this.props.appId !== prevProps.appId) {
			this.updateRating();
		}
	}

	setRating = async (ratingValue: number) => {
		const ratingTime = Date.now();

		await AppsStore.sendAppRatingData(this.props.appId, ratingValue);

		this.localStorageRating.set(this.props.appId, ratingValue, ratingTime);
		this.props.callback && this.props.callback({ chosenRating: ratingValue });
		this.setState({
			voteTime: ratingTime,
			appRating: ratingValue,
		});

		this.setState({
			hasVoted: true,
		});

		setTimeout(() => {
			this.setState({
				hasVoted: false,
			});
		}, VOTING_MESSAGE_VISIBILITY_TIMEOUT);
	};

	updateRating(): void {
		const localStorageRating = this.localStorageRating.get(this.props.appId);

		if (localStorageRating) {
			this.setState({
				appRating: localStorageRating.rating,
				voteTime: localStorageRating.time,
				hasVoted: false,
			});
		}
	}

	showTimeoutWarningMessage = (time: number, message: string) => {
		const diffTime = time - Date.now();
		const messageArr = [];
		const daysDiff = Math.floor(diffTime / (60 * 60 * 1000 * 24));

		let hoursDiff = diffTime / (60 * 60 * 1000);
		let minDiff = diffTime / (60 * 1000);
		let secDiff = diffTime / (1000);

		if (daysDiff >= 1) {
			const roundD = Math.floor(daysDiff * 10) / 10;
			messageArr.push(getLocalizedTime(roundD).days);
			hoursDiff = hoursDiff - roundD * 24;

			if (hoursDiff > 1) {
				messageArr.push(getLocalizedTime(Math.ceil(hoursDiff)).hours);
			}
		} else {
			if (hoursDiff > 1) {
				const roundH = Math.floor(hoursDiff);
				messageArr.push(getLocalizedTime(roundH).hours);
				minDiff = minDiff - roundH * 60;
			}
			if (minDiff > 1) {
				const roundM = Math.floor(minDiff);
				messageArr.push(getLocalizedTime(roundM).minutes);
				secDiff = secDiff - roundM * 60;
			}

			if (hoursDiff <= 1 && minDiff <= 1 && secDiff > 0) {
				messageArr.push(this.t('someSec').toLowerCase());
			}
		}

		// todo: заменить в будущем на попап
		alert(`${message} ${messageArr.join(' ')}`);
	};

	onClickRatingMarker = async (ratingValue: number) => {
		const votingCooldown = getConfig().apps.votingCooldown;
		const diffTime = Math.abs(Date.now() - this.state.voteTime);
		const isVotingAvailable = !this.state.voteTime || diffTime > votingCooldown;

		if (isVotingAvailable) {
			await this.setRating(ratingValue);
		} else {
			const availableTime = this.state.voteTime + votingCooldown;

			this.showTimeoutWarningMessage(availableTime, this.t('voteCoolDownMessage'));
		}
	};

	render() {
		const {
			className,
		} = this.props;

		return (
			<ContentPanel
				className={s.contentPanel}
				title={this.t('rateApp')}
				titlePart={this.state.hasVoted && <span className={s.successText}>{this.t('voteSuccess')}</span>}
			>
				<RatingBar
					appRating={this.state.appRating}
					className={classNames(s.block, className)}
					starClassName={s.star}
					onClickRatingMarker={this.onClickRatingMarker}
				/>
			</ContentPanel>
		);
	}

}
